/**
 * EditTemplateView - Full template editor with sections and items
 */
import React, { useState, useEffect } from 'react';
import {
  Save, Trash2, Plus, GripVertical,
  ChevronDown, ChevronRight, Edit2, AlertTriangle,
  X, Check
} from 'lucide-react';
import { generateSectionId, generateItemId } from '../../../../utils/familiarisationTemplateHelpers';
// FT-356c: back navigation moved to shell-level MobileBottomBar via
// NavigationBackContext. Header raw ArrowLeft back button removed.
import { useSetBackHandler } from '../../../../contexts/NavigationBackContext';

const VALIDITY_OPTIONS = [
  { value: 0, label: 'No expiry' },
  { value: 6, label: '6 months' },
  { value: 12, label: '12 months' },
  { value: 18, label: '18 months' },
  { value: 24, label: '24 months' },
  { value: 36, label: '36 months' }
];

const EditTemplateView = ({ template, onSave, onBack, onDelete }) => {
  // FT-356c: register parent's onBack as the shell-level back handler.
  useSetBackHandler(onBack);

  const [editedTemplate, setEditedTemplate] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  const [editingName, setEditingName] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editingSection, setEditingSection] = useState(null);
  const [showAddSection, setShowAddSection] = useState(false);
  const [newSectionName, setNewSectionName] = useState('');

  useEffect(() => {
    if (template) {
      setEditedTemplate(JSON.parse(JSON.stringify(template)));
      // Expand all sections by default
      const expanded = {};
      template.sections?.forEach(s => { expanded[s.id] = true; });
      setExpandedSections(expanded);
    }
  }, [template]);

  if (!editedTemplate) return null;

  const updateTemplate = (updates) => {
    setEditedTemplate({ ...editedTemplate, ...updates });
    setHasChanges(true);
  };

  const handleSave = () => {
    const updated = {
      ...editedTemplate,
      updatedAt: new Date().toISOString()
    };
    onSave(updated);
    setHasChanges(false);
  };

  const toggleSection = (sectionId) => {
    setExpandedSections({
      ...expandedSections,
      [sectionId]: !expandedSections[sectionId]
    });
  };

  // Section operations
  const addSection = () => {
    if (!newSectionName.trim()) return;

    const newSection = {
      id: generateSectionId(),
      name: newSectionName.trim(),
      order: (editedTemplate.sections?.length || 0) + 1,
      items: []
    };

    updateTemplate({
      sections: [...(editedTemplate.sections || []), newSection]
    });

    setExpandedSections({ ...expandedSections, [newSection.id]: true });
    setNewSectionName('');
    setShowAddSection(false);
  };

  const updateSection = (sectionId, updates) => {
    updateTemplate({
      sections: editedTemplate.sections.map(s =>
        s.id === sectionId ? { ...s, ...updates } : s
      )
    });
    setEditingSection(null);
  };

  const deleteSection = (sectionId) => {
    if (!window.confirm('Delete this section and all its items?')) return;
    updateTemplate({
      sections: editedTemplate.sections.filter(s => s.id !== sectionId)
    });
  };

  // Item operations
  const addItem = (sectionId) => {
    const newItem = {
      id: generateItemId(),
      text: 'New checklist item',
      required: false,
      regulation: 'Company',
      isCustom: true,
      isEdited: false,
      originalText: null
    };

    updateTemplate({
      sections: editedTemplate.sections.map(s =>
        s.id === sectionId
          ? { ...s, items: [...(s.items || []), newItem] }
          : s
      )
    });

    setEditingItem({ sectionId, itemId: newItem.id, text: newItem.text });
  };

  const updateItem = (sectionId, itemId, updates) => {
    updateTemplate({
      sections: editedTemplate.sections.map(s =>
        s.id === sectionId
          ? {
              ...s,
              items: s.items.map(item =>
                item.id === itemId ? { ...item, ...updates } : item
              )
            }
          : s
      )
    });
  };

  const deleteItem = (sectionId, itemId) => {
    updateTemplate({
      sections: editedTemplate.sections.map(s =>
        s.id === sectionId
          ? { ...s, items: s.items.filter(item => item.id !== itemId) }
          : s
      )
    });
  };

  const saveItemEdit = () => {
    if (!editingItem) return;
    updateItem(editingItem.sectionId, editingItem.itemId, { text: editingItem.text });
    setEditingItem(null);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center justify-between">
            {/* FT-356c: header raw ArrowLeft back button removed — bottom bar owns Back. */}
            <div className="flex items-center gap-3">
              {editingName ? (
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={editedTemplate.name}
                    onChange={(e) => updateTemplate({ name: e.target.value })}
                    className="text-lg font-semibold px-2 py-1 border border-cyan-500 rounded focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    autoFocus
                  />
                  <button
                    onClick={() => setEditingName(false)}
                    className="p-1 text-cyan-600 hover:bg-cyan-50 rounded"
                  >
                    <Check className="h-5 w-5" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <h1 className="text-lg font-semibold text-gray-900">
                    {editedTemplate.name}
                  </h1>
                  <button
                    onClick={() => setEditingName(true)}
                    className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                  >
                    <Edit2 className="h-4 w-4" />
                  </button>
                  {editedTemplate.isSystem && (
                    <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
                      System Template
                    </span>
                  )}
                </div>
              )}
            </div>

            <div className="flex items-center gap-3">
              {hasChanges && (
                <span className="text-sm text-amber-600 bg-amber-50 px-3 py-1 rounded-full">
                  Unsaved changes
                </span>
              )}
              {onDelete && !editedTemplate.isSystem && (
                <button
                  onClick={() => {
                    if (window.confirm('Delete this template? This cannot be undone.')) {
                      onDelete(editedTemplate.id);
                    }
                  }}
                  className="px-4 py-2 text-sm font-medium text-red-600 bg-white border border-red-300 rounded-lg hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4 inline mr-2" />
                  Delete
                </button>
              )}
              <button
                onClick={handleSave}
                disabled={!hasChanges}
                className={`px-4 py-2 text-sm font-medium text-white rounded-lg flex items-center gap-2 ${
                  hasChanges
                    ? 'bg-cyan-600 hover:bg-cyan-700'
                    : 'bg-gray-300 cursor-not-allowed'
                }`}
              >
                <Save className="h-4 w-4" />
                Save Changes
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Template Settings */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h3 className="text-sm font-medium text-gray-900 mb-4">Template Settings</h3>

          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-sm text-gray-600 mb-1">Validity Period</label>
              <select
                value={editedTemplate.validityPeriodMonths}
                onChange={(e) => updateTemplate({ validityPeriodMonths: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
              >
                {VALIDITY_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm text-gray-600 mb-1">Type</label>
              <select
                value={editedTemplate.type}
                onChange={(e) => updateTemplate({ type: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                disabled={editedTemplate.isSystem}
              >
                <option value="crew">Crew</option>
                <option value="contractor">Contractor</option>
                <option value="custom">Custom</option>
              </select>
            </div>
          </div>
        </div>

        {/* Sections */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">
              Sections & Items
            </h3>
            <span className="text-sm text-gray-500">
              {editedTemplate.sections?.length || 0} sections, {' '}
              {editedTemplate.sections?.reduce((sum, s) => sum + (s.items?.length || 0), 0) || 0} items
            </span>
          </div>

          {editedTemplate.sections?.map((section) => (
            <div key={section.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              {/* Section Header */}
              <div
                className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200 cursor-pointer"
                onClick={() => toggleSection(section.id)}
              >
                <div className="flex items-center gap-3">
                  <GripVertical className="h-4 w-4 text-gray-400" />
                  {expandedSections[section.id] ? (
                    <ChevronDown className="h-4 w-4 text-gray-400" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-gray-400" />
                  )}

                  {editingSection === section.id ? (
                    <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                      <input
                        type="text"
                        defaultValue={section.name}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            updateSection(section.id, { name: e.target.value });
                          }
                          if (e.key === 'Escape') {
                            setEditingSection(null);
                          }
                        }}
                        className="px-2 py-1 border border-cyan-500 rounded text-sm focus:outline-none"
                        autoFocus
                      />
                      <button
                        onClick={() => setEditingSection(null)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <span className="font-medium text-gray-900">{section.name}</span>
                  )}

                  <span className="text-sm text-gray-500">
                    ({section.items?.length || 0} items)
                  </span>
                </div>

                <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                  <button
                    onClick={() => setEditingSection(section.id)}
                    className="p-1 text-gray-400 hover:text-cyan-600 hover:bg-cyan-50 rounded"
                  >
                    <Edit2 className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => deleteSection(section.id)}
                    className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* Section Items */}
              {expandedSections[section.id] && (
                <div className="divide-y divide-gray-100">
                  {section.items?.map((item) => (
                    <div key={item.id} className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50">
                      <GripVertical className="h-4 w-4 text-gray-300" />

                      {editingItem?.itemId === item.id ? (
                        <div className="flex-1 flex items-center gap-2">
                          <input
                            type="text"
                            value={editingItem.text}
                            onChange={(e) => setEditingItem({ ...editingItem, text: e.target.value })}
                            className="flex-1 px-2 py-1 border border-cyan-500 rounded text-sm focus:outline-none"
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') saveItemEdit();
                              if (e.key === 'Escape') setEditingItem(null);
                            }}
                          />
                          <button onClick={saveItemEdit} className="text-cyan-600">
                            <Check className="h-4 w-4" />
                          </button>
                          <button onClick={() => setEditingItem(null)} className="text-gray-400">
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ) : (
                        <>
                          <span className="flex-1 text-sm text-gray-700">{item.text}</span>

                          <div className="flex items-center gap-2">
                            {item.regulation && item.regulation !== 'Company' && (
                              <span className="text-xs text-gray-400">{item.regulation}</span>
                            )}
                            {item.isCustom && (
                              <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-600 rounded">
                                Custom
                              </span>
                            )}
                            <button
                              onClick={() => setEditingItem({
                                sectionId: section.id,
                                itemId: item.id,
                                text: item.text
                              })}
                              className="p-1 text-gray-400 hover:text-cyan-600"
                            >
                              <Edit2 className="h-3 w-3" />
                            </button>
                            <button
                              onClick={() => deleteItem(section.id, item.id)}
                              className="p-1 text-gray-400 hover:text-red-600"
                            >
                              <Trash2 className="h-3 w-3" />
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}

                  {/* Add Item Button */}
                  <button
                    onClick={() => addItem(section.id)}
                    className="w-full px-4 py-3 text-sm text-cyan-600 hover:bg-cyan-50 flex items-center gap-2"
                  >
                    <Plus className="h-4 w-4" />
                    Add Item
                  </button>
                </div>
              )}
            </div>
          ))}

          {/* Add Section */}
          {showAddSection ? (
            <div className="bg-white rounded-lg shadow-sm border border-cyan-200 p-4">
              <div className="flex items-center gap-3">
                <input
                  type="text"
                  value={newSectionName}
                  onChange={(e) => setNewSectionName(e.target.value)}
                  placeholder="New section name..."
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') addSection();
                    if (e.key === 'Escape') {
                      setShowAddSection(false);
                      setNewSectionName('');
                    }
                  }}
                />
                <button
                  onClick={addSection}
                  className="px-4 py-2 text-sm font-medium text-white bg-cyan-600 rounded-lg hover:bg-cyan-700"
                >
                  Add
                </button>
                <button
                  onClick={() => {
                    setShowAddSection(false);
                    setNewSectionName('');
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setShowAddSection(true)}
              className="w-full py-4 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-cyan-400 hover:text-cyan-600 flex items-center justify-center gap-2"
            >
              <Plus className="h-5 w-5" />
              Add Section
            </button>
          )}
        </div>

        {/* WBC3 Warning for System Templates */}
        {editedTemplate.isSystem && (
          <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-800">System Template</p>
              <p className="text-sm text-amber-700 mt-1">
                This is a built-in template based on WBC3 requirements.
                Removing required items may affect vessel compliance.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EditTemplateView;
